import { useEffect, useRef } from 'react';
import { Spinner, Text, VStack } from '@chakra-ui/react';
import { createFileRoute, redirect, useRouter } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import AuthenticationPage from '@/components/templates/AuthenticationPage';
import queryKeys from '@/constants/queryKeys';
import useToast from '@/hooks/useToast';
import authMessages from '@/messages/auth';
import { verifyEmailToken } from '@/services/api/requests/user';

type VerifyEmailSearch = {
  email: string;
  verifyEmailToken: string;
};

const VerifyEmail = () => {
  const router = useRouter();
  const search = Route.useSearch();
  const queryClient = useQueryClient();
  const toast = useToast();
  const handledRef = useRef(false);

  const { mutateAsync: handleEmailVerification } = useMutation({
    mutationFn: async () =>
      verifyEmailToken({
        token: search.verifyEmailToken,
        email: search.email,
      }),
    onSuccess: (data) => {
      if (data?.success) {
        queryClient.invalidateQueries({ queryKey: [queryKeys.user.info] });
      }
    },
  });

  useEffect(() => {
    if (handledRef.current) return;
    handledRef.current = true;

    const finalize = async (status: 'success' | 'error') => {
      toast({
        status: status === 'success' ? 'success' : 'error',
        title: status === 'success' ? 'Email verified' : 'Verification failed',
        description: status === 'success' ? 'Your email has been verified.' : authMessages.emailVerificationError,
      });

      await router.navigate({ to: '/settings', replace: true });
    };

    void handleEmailVerification()
      .then(({ success }) => finalize(success ? 'success' : 'error'))
      .catch(() => finalize('error'));
  }, [handleEmailVerification, router, toast]);

  return (
    <AuthenticationPage>
      <VStack spacing={'24px'} marginTop={'40px'}>
        <Text
          variant={'urbanistSemiBold'}
          textColor={'text.veryLightBlue'}
          color={'background.lightBlue'}
          opacity={0.85}
          maxWidth={'400px'}
        >
          {authMessages.emailVerificationLoading}
        </Text>
        <Spinner color={'background.lightBlue'} size={'xl'} thickness={'3px'} speed={'0.8s'} />
      </VStack>
    </AuthenticationPage>
  );
};

export const Route = createFileRoute('/verify-email/')({
  staticData: { title: 'Verify Email' },
  component: VerifyEmail,
  validateSearch: (search: Record<string, unknown>): VerifyEmailSearch => ({
    email: typeof search.email === 'string' ? search.email : '',
    verifyEmailToken: typeof search.verifyEmailToken === 'string' ? search.verifyEmailToken : '',
  }),
  beforeLoad: async ({ context: { auth }, search }) => {
    if (!search.email || !search.verifyEmailToken) {
      throw redirect({ to: '/sign-in' });
    }

    if (!auth?.isAuthenticated) {
      const linkBack = `/verify-email?email=${encodeURIComponent(search.email)}&verifyEmailToken=${encodeURIComponent(search.verifyEmailToken)}`;

      throw redirect({
        to: '/sign-in',
        search: { linkBack, email: search.email },
      });
    }
  },
});
